import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FbLogo from '../../assets/fb-logo-white.svg';
import config from '../../config/Constant';
import { postRequestAsync } from '../../helpers/apiHandler';
import { FacebookAuthResponse } from '../../helpers/FacebookAuthResponse';
import useFacebookSDK from '../../hooks/useFacebookSDK';
import { showAlert } from '../../store/actions/alertAction';
import './FacebookLoginButton.scss';

const FacebookLoginButton = ({
  accessToken,
  onLoginSuccess,
  onLoginFailed,
  isGettingStarted = true,
  buttonText = 'Connect with Facebook',
  style = {},
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { isInitialized, error } = useFacebookSDK(process.env.REACT_APP_META_APP_ID);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.auth.user);

  const exchangeCode = useCallback(
    async (code) => {
      setIsLoading(true);
      try {
        const response = await postRequestAsync(
          '/v2/whatsapp/exchange-code',
          { code },
          accessToken.token
        );

        if (response.success) {
          dispatch(showAlert('Successfully connected with WhatsApp Cloud!', 'success'));
          onLoginSuccess();
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        onLoginFailed && onLoginFailed();
        dispatch(showAlert(err.message || 'Failed to connect', 'danger'));
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, dispatch, onLoginSuccess, onLoginFailed]
  );

  const handleLogin = useCallback(() => {
    if (isGettingStarted) {
      const basePath = role === 'manager' ? '/manager/dashboard/whatsapp' : '/dashboard/whatsapp';
      navigate(`${basePath}/getting-started`);
      return;
    }

    if (!isInitialized) return;

    window.FB.login((response) => {
      if (response.authResponse) {
        const fbAuthRes = new FacebookAuthResponse(response.authResponse);
        if (fbAuthRes.isValid()) {
          exchangeCode(fbAuthRes.getCode());
        }
      }
    }, config.whatsapp_embedded_config);
  }, [isInitialized, isGettingStarted, navigate, exchangeCode, role]);

  if (error) {
    return null;
  }

  return (
    <button
      className="facebook-login-button"
      onClick={handleLogin}
      disabled={!isInitialized || isLoading}
      style={style}
    >
      <div className="button-icon">
        <img src={FbLogo} alt="Facebook Logo" />
      </div>
      <div className="button-text">
        {isLoading ? 'Connecting...' : isInitialized ? buttonText : 'Loading...'}
      </div>
    </button>
  );
};

export default React.memo(FacebookLoginButton);
